import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { persistToken } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Loader2, Eye, EyeOff, Building, Phone, User, Mail, Lock, FileText, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    firm_name: '', branch_address: '', contact_name: '', email: '',
    phone: '', password: '', hmrc_arn: '', professional_body: '', membership_number: ''
  });

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/accountant/register`, form);
      if (res.data?.token) persistToken(res.data.token);
      toast.success('Practice registration submitted! Your account is pending verification.');
      navigate('/accountant/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: 'firm_name', label: 'Firm / Practice Name', icon: Building, placeholder: 'Smith & Co Accountants' },
    { key: 'branch_address', label: 'Branch Address', icon: Building, placeholder: '123 High Street, London EC1A' },
    { key: 'contact_name', label: 'Your Full Name', icon: User, placeholder: 'John Smith' },
    { key: 'email', label: 'Email Address', icon: Mail, placeholder: 'john@smithco.com', type: 'email' },
    { key: 'phone', label: 'Phone Number', icon: Phone, placeholder: '07700 900123' },
    { key: 'hmrc_arn', label: 'HMRC Agent Reference Number (ARN)', icon: FileText, placeholder: 'XARN0001234' },
    { key: 'professional_body', label: 'Professional Body', icon: Award, placeholder: 'ICAEW / ACCA / CIOT / AAT' },
    { key: 'membership_number', label: 'Membership Number', icon: FileText, placeholder: '12345678' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <Shield className="h-12 w-12 mx-auto mb-3 text-sky-400" />
          <h1 className="text-2xl font-bold text-white">Register Your Practice</h1>
          <p className="text-slate-400 mt-1 text-sm">Join the Seafarer Logbook Accountant Portal</p>
        </div>

        <Card className="bg-slate-800/90 border-slate-700 shadow-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Practice Details</CardTitle>
            <CardDescription className="text-slate-400 text-xs">
              All fields required. Your practice will be verified by our admin team.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              {fields.map(f => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-slate-300 text-xs">{f.label}</Label>
                  <div className="relative">
                    <f.icon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                    <Input
                      type={f.type || 'text'} placeholder={f.placeholder}
                      value={form[f.key]} onChange={update(f.key)}
                      className="pl-9 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-9 text-sm"
                      required data-testid={`register-${f.key}`}
                    />
                  </div>
                </div>
              ))}
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'} placeholder="Min 8 chars, 1 uppercase, 1 number"
                    value={form.password} onChange={update('password')}
                    className="pl-9 pr-9 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-9 text-sm"
                    required data-testid="register-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-sky-600 hover:bg-sky-700 h-10 text-sm font-medium mt-2"
                data-testid="register-submit">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Registering...</> : 'Register Practice'}
              </Button>
            </form>
            <p className="text-center text-slate-400 text-xs mt-4">
              Already registered?{' '}
              <Link to="/accountant/login" className="text-sky-400 hover:text-sky-300 font-medium">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccountantRegister;
